import { normalizeWordAttribution } from './wordAttribution.js';
import { applyPrepositionFromEnglish } from './englishPrepositions.js';
import { taxonomyPayload } from './wordTaxonomy.js';

/**
 * Typographic / curly apostrophes and lookalikes → ASCII apostrophe (').
 * Covers Word/Docs paste: ’ ‘ ‛ ‚ ′ ʼ ＇ plus grave ` and acute ´
 */
const CURLY_APOSTROPHE = /[\u2018\u2019\u201A\u201B\u2032\u02BC\uFF07\u0060\u00B4]/g;

export function normalizeWordApostrophe(text) {
    if (typeof text !== 'string' || !text) return text;
    return text.replace(CURLY_APOSTROPHE, "'");
}

/** Normalize English (and spelling_variation) on a word-shaped object. */
export function withNormalizedEnglish(payload) {
    if (!payload || typeof payload !== 'object') return payload;
    let next = payload;
    if (typeof next.english === 'string') {
        const english = normalizeWordApostrophe(next.english);
        if (english !== next.english) next = { ...next, english };
    }
    if (typeof next.spelling_variation === 'string') {
        const spelling_variation = normalizeWordApostrophe(next.spelling_variation);
        if (spelling_variation !== next.spelling_variation) next = { ...next, spelling_variation };
    }
    return next;
}

const WORD_DB_TABLES = new Set(['globalWords', 'groupWords', 'showGames', 'global_words', 'group_words', 'show_games']);

/** Normalize english + curriculum attribution + bracket preposition on word rows. */
export function normalizeNewWordPayload(tableName, payload) {
    if (!payload || !WORD_DB_TABLES.has(tableName)) return payload;
    let next = withNormalizedEnglish(payload);
    if (typeof next.english === 'string') {
        next = applyPrepositionFromEnglish(next);
    }
    next = normalizeWordAttribution(next);
    const hasTaxonomy = ['theme_pairs', 'themePairs', 'subtypes', 'theme', 'subtheme', 'subtype']
        .some((field) => Object.prototype.hasOwnProperty.call(next, field));
    if (!hasTaxonomy) return next;
    const normalized = { ...next, ...taxonomyPayload(next) };
    delete normalized.themePairs;
    delete normalized.taxonomyPair;
    return normalized;
}

/**
 * Apply preposition extraction whenever english is present (updates / upserts).
 * If `preposition` is already set on the payload (manual edit / import column), keep it.
 */
export function withPrepositionFromEnglish(payload) {
    if (!payload || typeof payload.english !== 'string') return payload;
    let next = withNormalizedEnglish(payload);
    if (Object.prototype.hasOwnProperty.call(next, 'preposition')) return next;
    return applyPrepositionFromEnglish(next);
}
